import React, { useState } from "react";
import { ShoppingCart } from "lucide-react";
import { Order } from "../../../Admin/StockOrder/OrderInfo/types";
import toast from "react-hot-toast";

interface OrderSummaryProps {
  totalItems: number;
  totalAmount: number;
  onNotifyPatient: () => void;
  actionText?: string;
  order: Order[];
  setExtraDiscount: React.Dispatch<React.SetStateAction<number>>;
  totalPrice: number;
  totalDiscount: number;
  isBillOpen?: boolean;
  roundoffValue?: number;
  roundedAmount: number;
  cashAmount?: number;
  paymentType?: string;
  coins?: number;
  coinsused?: number | '';
  setCoinsused: React.Dispatch<React.SetStateAction<number|"">>;
  isProcessing?: boolean;
}

const ClinicOrderSummary: React.FC<OrderSummaryProps> = ({
  totalItems,
  totalAmount,
  onNotifyPatient,
  actionText = "Proceed",
  order,
  setExtraDiscount,
  totalPrice,
  totalDiscount,
  isBillOpen,
  roundedAmount,
  roundoffValue,
  cashAmount,
  paymentType,
  coins,
  coinsused,
  setCoinsused,
  isProcessing,
}) => {
  const [discount, setDiscount] = useState<string>("");
  const [showDiscountInput, setShowDiscountInput] = useState(false);

  const handleProceed = () => {
    setExtraDiscount(parseFloat(discount) || 0);
    onNotifyPatient();
  };

  const calculateFinalAmount = () => {
    const baseAmount =
      order[0]?.orderTotal != null ? order[0].orderTotal : roundedAmount;
    const discountValue = parseFloat(discount) || 0;
    return Math.max(0, baseAmount - discountValue - Number(coinsused || 0));
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow: empty string, numbers, and single decimal point
    if (value === "" || /^(\d+\.?\d*|\.\d+)$/.test(value)) {
      setDiscount(value);
    }
  };

  const handleCoinsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if(Number(value)>(coins||0)){
      toast.error("Coins cannot be greater than coins in wallet");
    }else{
      setCoinsused(Number(value));
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 w-full max-w-md">
      {/* Top Row - Items */}
      <div className="flex items-center justify-between border-b pb-2 mb-2">
        <div className="flex items-center gap-2 text-gray-700">
          <ShoppingCart className="w-4 h-4" />
          <span className="text-sm font-medium">{totalItems} items</span>
        </div>
        <div className="flex flex-row gap-2">
          <p className="text-xs text-gray-500">Coins in wallet : </p>
          <p className="text-xs text-gray-900">₹{coins}</p>
        </div>
      </div>

      <div className="mb-2 text-sm text-gray-600 flex flex-row justify-between">
        <p>Total Amount: </p>
        <p>₹{totalPrice.toFixed(2)}</p>
      </div>
      <div className="mb-2 text-sm text-gray-600 flex flex-row justify-between">
        <p>Total Discount: </p>
        <p>₹{(totalDiscount + Number(discount)).toFixed(2)}</p>
      </div>
      <div className="mb-2 text-sm text-gray-600 flex flex-row justify-between">
        <p>Round off : </p>
        <p>₹{roundoffValue}</p>
      </div>

      {<div className="mb-2 text-sm text-gray-600 flex justify-between items-center">
        <p>Coins :</p>

        <input
          type="number"
          value={coinsused}
          max={coins}
          disabled={isBillOpen || coins === 0}
          onChange={(e) =>
            handleCoinsChange(e)
          }
          className="border rounded px-2 py-1 w-24 text-right"
          placeholder="Enter coins"
        />
      </div>}

      {/* Discount Row */}
      {!isBillOpen && (
        <div className="mb-3">
          {showDiscountInput ? (
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={discount}
                onChange={handleDiscountChange}
                className="border rounded-md px-2 py-1 text-sm w-24"
                placeholder="0.00"
                inputMode="decimal" // Shows decimal keyboard on mobile
              />
              <button
                onClick={() => {
                  setShowDiscountInput(false);
                  setDiscount("");
                }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowDiscountInput(true)}
              className="text-xs text-blue-600 hover:text-blue-800 mb-2"
            >
              + Add extra discount
            </button>
          )}
        </div>
      )}

      {/* Bottom Row - Total & Button */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-gray-500">Total payable</div>
          <div className="text-xl font-semibold text-gray-900">
            ₹{calculateFinalAmount().toFixed(2)}
          </div>
          {paymentType === "Split" && (
            <div>
              <div className="flex flex-row">
                <div className="text-xs text-gray-500">Cash Amount</div>
                <div className="text-xs text-gray-900">
                  ₹{Number(cashAmount || 0)?.toFixed(2)}
                </div>
              </div>
              <div className="flex flex-row">
                <div className="text-xs text-gray-500">Online Amount</div>
                <div className="text-xs text-gray-900">
                  ₹
                  {(calculateFinalAmount() - Number(cashAmount || 0))?.toFixed(
                    2,
                  )}
                </div>
              </div>
            </div>
          )}
          {discount && parseFloat(discount) > 0 && (
            <div
              className={`text-xs ${
                Number(discount) > roundedAmount
                  ? "text-red-600"
                  : "text-green-600"
              } `}
            >
              {Number(discount) > roundedAmount
                ? "Discount greater than total amount"
                : `-₹${parseFloat(discount).toFixed(2)} discount applied`}
            </div>
          )}
        </div>
        <button
          onClick={handleProceed}
          disabled={isProcessing}
          className={`bg-green-600 text-white px-4 py-2 rounded-md transition-colors text-sm
    ${isProcessing ? "opacity-50 cursor-not-allowed" : "hover:bg-green-700"}`}
        >
          {isProcessing ? "Processing..." : actionText}
        </button>
      </div>
    </div>
  );
};

export default ClinicOrderSummary;
